import { CourseType } from "@core/schema/CourseType.js";
import { Locale } from "@core/schema/Locale.js";
import homeEspressoCs from "./content/courses/home-espresso.cs.txt?raw";
import homeEspressoEn from "./content/courses/home-espresso.en.txt?raw";
import homeFilterCoffeeCs from "./content/courses/home-filter-coffee.cs.txt?raw";
import homeFilterCoffeeEn from "./content/courses/home-filter-coffee.en.txt?raw";
import scaFoundationCs from "./content/courses/sca-foundation.cs.txt?raw";
import scaFoundationEn from "./content/courses/sca-foundation.en.txt?raw";
import scaIntermediateCs from "./content/courses/sca-intermediate.cs.txt?raw";
import scaIntermediateEn from "./content/courses/sca-intermediate.en.txt?raw";

export interface CourseContentDisclosure {
	title: string;
	content: string;
}

export interface CourseContentSections {
	/** Intro copy and the first section heading, meant to sit at the top with a read-more toggle. */
	intro: string;
	/** Every section heading after the first one, meant to be rendered as disclosures. */
	disclosures: CourseContentDisclosure[];
}

function parseCourseContent(raw: string): CourseContentSections {
	const blocks = raw
		.trim()
		.split(/\n\s*\n/u)
		.map((block) => block.trim());

	const introParts: string[] = [];
	const disclosures: CourseContentDisclosure[] = [];
	let currentDisclosure: { title: string; content: string[] } | undefined;
	let sawFirstHeading = false;

	function flushCurrentDisclosure(): void {
		if (currentDisclosure) {
			disclosures.push({
				title: currentDisclosure.title,
				content: currentDisclosure.content.map((paragraph) => `p[[${paragraph}]]`).join(""),
			});
			currentDisclosure = undefined;
		}
	}

	for (const block of blocks) {
		const headingMatch = /^heading\[\[(?<title>.+)\]\]$/u.exec(block);
		if (headingMatch) {
			flushCurrentDisclosure();
			const title = headingMatch.groups?.title?.trim() ?? "";
			if (sawFirstHeading) {
				currentDisclosure = { title, content: [] };
			} else {
				sawFirstHeading = true;
				introParts.push(`heading[[${title}]]`);
			}
			continue;
		}

		if (currentDisclosure) {
			currentDisclosure.content.push(block);
		} else {
			introParts.push(`p[[${block}]]`);
		}
	}
	flushCurrentDisclosure();

	return { intro: introParts.join(""), disclosures };
}

export const courseTypeDescriptionMap: Record<CourseType, Record<Locale, CourseContentSections>> = {
	[CourseType.HomeFilterCoffee]: {
		[Locale.cs]: parseCourseContent(homeFilterCoffeeCs),
		[Locale.en]: parseCourseContent(homeFilterCoffeeEn),
	},
	[CourseType.HomeEspresso]: {
		[Locale.cs]: parseCourseContent(homeEspressoCs),
		[Locale.en]: parseCourseContent(homeEspressoEn),
	},
	[CourseType.SCAFoundation]: {
		[Locale.cs]: parseCourseContent(scaFoundationCs),
		[Locale.en]: parseCourseContent(scaFoundationEn),
	},
	[CourseType.SCAIntermediate]: {
		[Locale.cs]: parseCourseContent(scaIntermediateCs),
		[Locale.en]: parseCourseContent(scaIntermediateEn),
	},
};
